/**
 * Tariff detail drawer: shared DetailDrawer + DetailDrawerContent.
 * Read-only for now — create/delete BE endpoints are missing (404).
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Table from '@mui/material/Table';
import Stack from '@mui/material/Stack';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import { getCurrencySymbolForLocation } from '../utils/currency';
import DetailDrawer from '../components/DetailDrawer';
import { Section, Field } from '../components/DetailDrawerContent';

/** Regional indicator symbols for 2-letter country code (e.g. RU → 🇷🇺) */
function getCountryFlagEmoji(code) {
  if (!code || typeof code !== 'string' || code.length !== 2) return null;
  const a = code.toUpperCase();
  const c1 = a.charCodeAt(0) - 0x41;
  const c2 = a.charCodeAt(1) - 0x41;
  if (c1 < 0 || c1 > 25 || c2 < 0 || c2 > 25) return null;
  return String.fromCodePoint(0x1f1e6 + c1, 0x1f1e6 + c2);
}

export default function TariffDetailDrawer({ tariff, open, onClose }) {
  const { t } = useTranslation();
  const currency = getCurrencySymbolForLocation(tariff?.location);
  const flag = getCountryFlagEmoji(tariff?.location);
  const parts = Array.isArray(tariff?.parts) ? tariff.parts : [];

  return (
    <DetailDrawer title={t('drawer.tariff.title')} open={open} onClose={onClose}>
      <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
        {!tariff ? (
          <Typography color="text.secondary">{t('drawer.tariff.noSelection')}</Typography>
        ) : (
          <>
            <Section title={t('drawer.tariff.details')}>
              <Field label={t('drawer.tariff.name')} value={tariff.name ?? '—'} />
              <Field
                label={t('drawer.tariff.location')}
                value={
                  tariff.location
                    ? (
                      <Stack direction="row" alignItems="center" gap={0.75}>
                        {flag ? <span aria-hidden>{flag}</span> : null}
                        <span>{tariff.location}</span>
                      </Stack>
                    )
                    : '—'
                }
              />
              <Field label={t('drawer.tariff.tariffType')} value={tariff.tariffType ?? '—'} />
              <Field
                label={t('drawer.tariff.holdingAmount')}
                value={tariff.holdAmount != null ? `${tariff.holdAmount} ${currency}` : '—'}
              />
              <Field
                label={t('drawer.tariff.penaltyAmount')}
                value={tariff.penaltyAmount != null ? `${tariff.penaltyAmount} ${currency}` : '—'}
              />
            </Section>

            {parts.length > 0 && (
              <Section title={t('drawer.tariff.periods')}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>{t('drawer.tariff.periodName')}</TableCell>
                      <TableCell>{t('drawer.tariff.periodTime')}</TableCell>
                      <TableCell>{t('drawer.tariff.periodPrice')}</TableCell>
                      <TableCell>{t('drawer.tariff.periodCount')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {parts.map((row, idx) => (
                      <TableRow key={row.order ?? idx}>
                        <TableCell>{row.name ?? '—'}</TableCell>
                        <TableCell>
                          {row.timePeriod != null && row.timeUnit
                            ? `${row.timePeriod} ${row.timeUnit}`
                            : '—'}
                        </TableCell>
                        <TableCell>
                          {row.price === 0 ? t('drawer.tariff.free') : `${row.price} ${currency}`}
                        </TableCell>
                        <TableCell>{row.periodCount ?? '—'}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Section>
            )}
          </>
        )}
      </Box>
    </DetailDrawer>
  );
}
